import { FC } from 'react';
import { useAtomValue } from 'jotai';
import { InlineNotification } from '@carbon/react';
import { describeRegenerationScope, unitRegenerationAtom } from './atoms';

/**
 * The live "re-slotting the unit" strip shown while a rotate's regeneration runs.
 *
 * Reads the one shared tally (`unitRegenerationAtom`, advanced by
 * `useUnitRegenerationProgress`), so it can sit in BOTH the builder modal and the
 * main page and show the same progress. Renders nothing when no regeneration is
 * in flight; turns success/error once every formation has reported, and clears
 * itself shortly after.
 *
 * @return {JSX.Element | null} The notification, or null when idle.
 */
export const UnitRegenerationBanner: FC = () => {
  const regeneration = useAtomValue(unitRegenerationAtom);
  if (!regeneration) return null;

  const {
    totalZonesReported,
    totalZones,
    totalFormations,
    failedZones,
    done,
    message,
    formations,
  } = regeneration;
  // "Y inventories for X formations" (product wording, Misha): every line names both grains —
  // the inventory runs being re-laid and the distinct formations they lay.
  const scope = describeRegenerationScope(totalZones, totalFormations);

  if (done) {
    const ok = failedZones === 0;
    return (
      <InlineNotification
        kind={ok ? 'success' : 'error'}
        lowContrast
        hideCloseButton
        title={
          ok
            ? `Slot inventory regenerated across all ${scope}.`
            : `Slot regeneration finished — ${failedZones} of ${scope} failed.`
        }
        data-testid="unit-regeneration-banner"
      />
    );
  }

  return (
    <InlineNotification
      kind="info"
      lowContrast
      hideCloseButton
      title={message}
      subtitle={`${totalZonesReported} of ${scope} done${
        formations.length ? ` (${formations.join(', ')})` : ''
      }. Slots refresh once every formation has finished.`}
      data-testid="unit-regeneration-banner"
    />
  );
};
